import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * Persistence for a try-on visit: the generated results, and the customer's own
 * photo.
 *
 * iOS purges backgrounded standalone PWAs from memory readily — returning is a
 * cold start, not a resumed session. Without this, a customer who glances at a
 * notification loses every image the store has already paid Gemini for, and
 * there is no way to recover one: the API stores nothing and there is no job id
 * to poll.
 *
 * Results are stored as Blobs rather than the base64 data URL the API returns.
 * Base64 is ~33% larger than the bytes it encodes and has to be held as a
 * string; a Blob can live on disk and is handed to `<img>` via an object URL.
 *
 * ⚠️ **Scope is the visit, not the photo — that changed in v2, and it is the
 * whole point of the rewrite.** Retaking a photo used to wipe every result,
 * because a result was considered to belong to the photo it came from. It now
 * belongs to the *visit*, so a customer can retake and still compare against
 * what they already generated. `photoKey` survives as an attribute of each row
 * rather than as its scope.
 *
 * 🔒 **The photo store holds a face photo of a member of the public** (G28).
 * That is new — the bytes used to leave the device and never come back. It is
 * here so the try-on screen can show the customer their own photo, which needs
 * no readable url and no second CORS rule, and it is why `endVisit` clearing
 * this store is not optional housekeeping. `replacePhoto` deletes the one it
 * supersedes for the same reason: a visit keeps at most one photo on disk.
 */

export interface StoredResult {
  /** `${visitId}::${photoKey}::${garmentId}` — one result per garment per photo. */
  id: string;
  visitId: string;
  photoKey: string;
  /**
   * ⚠️ **The garment's id for a catalogue result, its R2 `garmentKey` for an
   * ad-hoc one.** An opaque identity either way, and the two cannot collide — a
   * catalogue id is a UUID and an ad-hoc key is a path beginning `garments/`.
   * That disjointness is why this column needed no change when ad-hoc landed.
   */
  garmentId: string;
  garmentName: string;
  /**
   * Set on **ad-hoc results only**, and its presence is what tells the two kinds
   * apart when the rail is rehydrated after a reload.
   *
   * ⚠️ Stored rather than re-derived from the key, because it is not in the key:
   * an ad-hoc garment has no row, so the category the salesman picked exists
   * nowhere else once the request is over. Absent on every catalogue result and
   * on any ad-hoc one written before this field existed — such a row still
   * restores, as a finished card, which is the only state a restored row is ever
   * in.
   */
  adHocCategory?: string;
  image: Blob;
  createdAt: number;
}

export interface StoredPhoto {
  /** The photo key — already unique, and what the results reference. */
  photoKey: string;
  visitId: string;
  image: Blob;
  createdAt: number;
}

interface TryOnDB extends DBSchema {
  results: {
    key: string;
    value: StoredResult;
    indexes: { 'by-visit': string };
  };
  photos: {
    key: string;
    value: StoredPhoto;
    indexes: { 'by-visit': string };
  };
}

const DB_NAME = 'shringaar-tryon';

/**
 * ⚠️ **v2 drops whatever v1 held rather than migrating it.**
 *
 * v1 scoped results by photo and had no photo store, so there is no mapping from
 * its rows to a visit that would not be invented. Nothing is lost that matters:
 * results only ever exist for the length of one customer's visit, and an upgrade
 * happens on a page load — a moment when no visit is mid-flight.
 */
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<TryOnDB>> | null = null;

function db(): Promise<IDBPDatabase<TryOnDB>> {
  dbPromise ??= openDB<TryOnDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      // Idempotent: an upgrade from v1 finds `results` present, a fresh install
      // finds neither.
      if (database.objectStoreNames.contains('results')) {
        database.deleteObjectStore('results');
      }
      if (database.objectStoreNames.contains('photos')) {
        database.deleteObjectStore('photos');
      }

      database.createObjectStore('results', { keyPath: 'id' }).createIndex('by-visit', 'visitId');
      database
        .createObjectStore('photos', { keyPath: 'photoKey' })
        .createIndex('by-visit', 'visitId');
    },
  });
  return dbPromise;
}

export function resultId(visitId: string, photoKey: string, garmentId: string): string {
  return `${visitId}::${photoKey}::${garmentId}`;
}


export async function saveResult(result: StoredResult): Promise<void> {
  try {
    await (await db()).put('results', result);
  } catch {
    // Storage full, private mode, or a blocked upgrade. The result is still in
    // memory for this session — persistence is a safety net, not the source of
    // truth, so failing to write must never break the flow.
  }
}

export async function loadResultsForVisit(visitId: string): Promise<StoredResult[]> {
  try {
    const rows = await (await db()).getAllFromIndex('results', 'by-visit', visitId);
    return rows.sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

/**
 * 🔒 Stores the customer's own photo so the try-on screen can show it back to
 * them. Same swallow-on-failure rule as `saveResult` — a device that cannot
 * write must still be able to run a visit, it just will not render the photo.
 */
export async function savePhoto(photo: StoredPhoto): Promise<void> {
  try {
    await (await db()).put('photos', photo);
  } catch {
    /* see saveResult */
  }
}

export async function loadPhoto(photoKey: string): Promise<Blob | null> {
  try {
    return (await (await db()).get('photos', photoKey))?.image ?? null;
  } catch {
    return null;
  }
}

/**
 * 🔒 Drops every photo in the visit except `keepPhotoKey` — called on retake, so
 * a visit holds at most one face photo on disk at a time.
 *
 * The results generated *from* a superseded photo are deliberately untouched:
 * they are the whole reason retake keeps working, and a generated try-on is not
 * the same artefact as the raw capture it came from.
 */
export async function prunePhotos(visitId: string, keepPhotoKey: string): Promise<void> {
  try {
    const database = await db();
    const rows = await database.getAllFromIndex('photos', 'by-visit', visitId);
    await Promise.all(
      rows
        .filter((row) => row.photoKey !== keepPhotoKey)
        .map((row) => database.delete('photos', row.photoKey)),
    );
  } catch {
    /* nothing recoverable to do */
  }
}

/**
 * 🔒 End of visit — **both stores**, unconditionally.
 *
 * Not scoped to one visit id on purpose: a visit id that was lost (private mode
 * refusing sessionStorage, a cleared tab) would otherwise strand its rows on the
 * device forever, and those rows include a customer's face photo. Clearing
 * everything is correct because one device runs one visit at a time.
 */
export async function clearVisit(): Promise<void> {
  try {
    const database = await db();
    await Promise.all([database.clear('results'), database.clear('photos')]);
  } catch {
    /* nothing recoverable to do */
  }
}
